import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { Form, Input, Button, message, Spin, Divider, Modal } from 'antd-v5';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { SettingsContent } from '../../components/SettingsContent';
import AudienceTagSelector from '../components/AudienceTagSelector';
import EventSessionForm from '../components/EventSessionForm';
import {
  useEvent,
  useEventSessions,
  useEventTags,
  useCreateEvent,
  useUpdateEvent,
  useReplaceEventTags,
  useArchiveEvent,
} from '../../../../../hooks/query/useEvents';
import './styles.scss';

const AddEditEvent = () => {
  const history = useHistory();
  const { eventId } = useParams();
  const { events: eventsFlag } = useFlags();

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }
  const [form] = Form.useForm();
  const isEditMode = !!eventId;

  // Local state for sessions - start empty (sessions are optional)
  const [sessions, setSessions] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [audienceCount, setAudienceCount] = useState(0);

  // Fetch event data if editing
  const { data: eventData, isLoading: isLoadingEvent } = useEvent({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  const { data: eventSessions, isLoading: isLoadingSessions } = useEventSessions({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  const { data: eventTags, isLoading: isLoadingTags } = useEventTags({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  // Mutations
  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const replaceTagsMutation = useReplaceEventTags();
  const archiveEventMutation = useArchiveEvent();

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && eventData) {
      form.setFieldsValue({
        name: eventData.name,
        description: eventData.description,
      });
    }
  }, [eventData, isEditMode, form]);

  // Populate sessions when editing
  useEffect(() => {
    // API returns sessions as direct array, not wrapped in .data
    const sessionsArray = eventSessions?.data || eventSessions;
    if (isEditMode && Array.isArray(sessionsArray) && sessionsArray.length > 0) {
      setSessions(
        sessionsArray.map(session => ({
          id: session.id,
          date: session.sessionDate || session.date, // API returns sessionDate
          startTime: session.startTime || session.start_time,
          endTime: session.endTime || session.end_time,
          storeIds: session.storeIds || session.stores?.map(s => s.id) || [], // API returns storeIds directly
        })),
      );
    }
  }, [eventSessions, isEditMode]);

  // Populate tags when editing
  useEffect(() => {
    if (isEditMode && eventTags?.data?.length > 0) {
      setSelectedTagIds(eventTags.data.map(tag => tag.id));
    }
  }, [eventTags, isEditMode]);

  const isLoading = isLoadingEvent || isLoadingSessions || isLoadingTags;
  const isSaving = createEventMutation.isLoading || updateEventMutation.isLoading;

  const handleAddSession = () => {
    setSessions(prev => [
      ...prev,
      { id: `temp-${Date.now()}`, date: null, startTime: null, endTime: null, storeIds: [] },
    ]);
  };

  const handleUpdateSession = (index, updatedSession) => {
    setSessions(prev => {
      const newSessions = [...prev];
      newSessions[index] = { ...newSessions[index], ...updatedSession };
      return newSessions;
    });
  };

  const handleRemoveSession = index => {
    setSessions(prev => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    if (isEditMode) {
      history.push(`/Configuration/events/event-details/${eventId}`);
    } else {
      history.push('/Configuration/events');
    }
  };

  const handleArchive = () => {
    Modal.confirm({
      title: 'Archive Event',
      content: 'Are you sure you want to permanently archive this event?',
      okText: 'Archive',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await archiveEventMutation.mutateAsync(eventId);
          message.success('Event archived successfully');
          history.push('/Configuration/events');
        } catch (error) {
          console.error('Error archiving event:', error);
          message.error('Failed to archive event. Please try again.');
        }
      },
    });
  };

  const handleSubmit = async values => {
    try {
      // Filter valid sessions
      const validSessions = sessions.filter(s => s.date && s.startTime && s.endTime);

      // Validate at least one session is required
      if (validSessions.length === 0) {
        message.error('Please add at least one event date and time');
        return;
      }

      // Validate at least one tag is required for audience
      if (selectedTagIds.length === 0) {
        message.error('Please select at least one tag for the audience');
        return;
      }

      const eventPayload = {
        name: values.name,
        description: values.description || '',
        sessions: validSessions.map(s => ({
          sessionDate: s.date,
          startTime: s.startTime,
          endTime: s.endTime,
          storeIds: s.storeIds || [],
        })),
      };

      let savedEventId = eventId;

      if (isEditMode) {
        await updateEventMutation.mutateAsync({
          eventId,
          data: { name: values.name, description: values.description },
        });
      } else {
        const result = await createEventMutation.mutateAsync(eventPayload);
        savedEventId = result?.id || result?.data?.id;
      }

      // Update tags if we have a saved event ID (always call to allow removing all tags)
      if (savedEventId) {
        await replaceTagsMutation.mutateAsync({
          eventId: savedEventId,
          tagIds: selectedTagIds,
        });
      }

      message.success(isEditMode ? 'Event updated successfully' : 'Event created successfully');
      if (isEditMode) {
        history.push(`/Configuration/events/event-details/${eventId}`);
      } else {
        history.push('/Configuration/events');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      message.error('Failed to save event. Please try again.');
    }
  };

  const breadcrumb = [
    { title: 'Setup' },
    { path: '/Configuration/events', title: 'Events' },
    { title: isEditMode ? 'Edit Event' : 'New Event' },
  ];

  // Header actions for StandardLayoutV2
  const headerActions = (
    <div className="add-edit-event-header-actions">
      {isEditMode && (
        <Button danger onClick={handleArchive} loading={archiveEventMutation.isLoading}>
          Archive
        </Button>
      )}
      <Button type="primary" onClick={() => form.submit()} loading={isSaving}>
        Save
      </Button>
      <Button onClick={handleCancel}>Cancel</Button>
    </div>
  );

  if (isEditMode && isLoading) {
    return (
      <StandardLayoutV2
        title={isEditMode ? 'Edit Event' : 'New Event'}
        breadcrumb={breadcrumb}
        classProps="flex-col-left"
        maxWidth="920px"
        styleProps={{ justifyContent: 'flex-start' }}
        containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      >
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title={isEditMode ? 'Edit Event' : 'New Event'}
      breadcrumb={breadcrumb}
      classProps="flex-col-left"
      maxWidth="920px"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      actions={headerActions}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ width: '100%' }}>
        {/* Event Details Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'calendar-star']} />}
          title="Event Details"
          description="Give your event a name to help identify it"
        >
          <Form.Item
            name="name"
            label="Event Name"
            rules={[{ required: true, message: 'Please enter an event name' }]}
            style={{ marginBottom: 0, width: 550 }}
          >
            <Input placeholder="Enter an Event Name" maxLength={60} showCount style={{ width: '100%' }} />
          </Form.Item>
        </SettingsContent>

        <Divider style={{ margin: '40px 0' }} />

        {/* Audience Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'users']} />}
          title={
            <span className="audience-title-row">
              Audience <span className="audience-count">{audienceCount.toLocaleString()} clients</span>
            </span>
          }
          description="Identify the target audience for this event using tags. Each tag will add people to the audience."
        >
          <AudienceTagSelector
            selectedTagIds={selectedTagIds}
            onTagsChange={setSelectedTagIds}
            onCountChange={setAudienceCount}
          />
        </SettingsContent>

        <Divider style={{ margin: '40px 0' }} />

        {/* Event Dates and Times Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'calendar']} />}
          title="Event Dates and Times"
          description="Set up where and when clients can book appointments for this event."
        >
          {sessions.length > 0 &&
            sessions.map((session, index) => (
              <EventSessionForm
                key={session.id}
                session={session}
                index={index}
                onUpdate={data => handleUpdateSession(index, data)}
                onRemove={() => handleRemoveSession(index)}
                canRemove
              />
            ))}

          <Button
            type="dashed"
            onClick={handleAddSession}
            icon={<FontAwesomeIcon icon={['fal', 'plus']} />}
            className={sessions.length > 0 ? 'm-top-16' : ''}
          >
            Add Dates
          </Button>
        </SettingsContent>
      </Form>
    </StandardLayoutV2>
  );
};

export default AddEditEvent;
